import React from "react";
import { motion } from "framer-motion";
import { ExternalLink, Headphones, Mic2, Guitar, Lock } from "lucide-react";
import { SongTabsSection } from "@/components/dawg/SongTabsSection";

export const DAWG_APP_URL = "https://wolf-learn-the-songs.kw2tgpwmgc.chatgpt.site/";

const FEATURES = [
  { icon: Headphones, title: "Listen", text: "Stream the available WOLF recordings straight from the music room. Turn it up." },
  { icon: Mic2, title: "Follow The Lyrics", text: "Every word, line by line, so you can sing along and learn the songs properly." },
  { icon: Guitar, title: "Practise", text: "Run your favourites again and again — then grab the basic guitar tabs below and play along." },
];

const COVERS = ["/godwolf-cover.jpg", "/peace-with-teeth-cover.jpg", "/philida-cover.jpg", "/cocaine-wolf-cover.jpg", "/no-more-fear-cover.jpg", "/inevitable-addiction-cover.jpg"];

export default function WolfDawg() {
  return (
    <div className="pt-32 sm:pt-40 pb-20 bg-[#080808] min-h-screen" data-testid="wolf-dawg-page">
      <div className="max-w-[1500px] mx-auto px-6 sm:px-10 lg:px-16">
        <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-center">
          <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8 }} className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.32em] text-[#D4AF37] mb-6">— Wolf Music Presents</p>
            <h1 className="font-display wolf-gold-foil text-6xl sm:text-8xl lg:text-9xl leading-[0.85]" data-testid="wolf-dawg-heading">WOLF DAWG</h1>
            <p className="font-display text-2xl sm:text-3xl text-white tracking-[0.15em] mt-8" data-testid="wolf-dawg-tagline">
              Step inside the WOLF music room. Turn it up.
            </p>
            <p className="font-editorial italic text-xl sm:text-2xl text-white/60 mt-6 max-w-2xl" data-testid="wolf-dawg-description">
              Explore the WOLF song collection. Listen to available recordings, follow the lyrics and practise your favourites.
            </p>

            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-5">
              <a
                href={DAWG_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                data-testid="open-wolf-dawg-btn"
                className="inline-flex items-center justify-center gap-3 btn-gold-fill text-black font-display tracking-[0.4em] text-base sm:text-lg px-10 py-6 border border-[#D4AF37] transition-all w-full sm:w-auto"
              >
                OPEN WOLF DAWG <ExternalLink size={18} />
              </a>
              <p className="text-[10px] uppercase tracking-[0.28em] text-white/45 flex items-center gap-2" data-testid="wolf-dawg-new-tab-note">
                <ExternalLink size={12} className="text-[#D4AF37]" /> Opens in a new tab
              </p>
            </div>
            <p className="mt-5 text-xs text-white/40 flex items-start gap-2 max-w-xl" data-testid="wolf-dawg-signin-note">
              <Lock size={12} className="mt-0.5 shrink-0 text-[#D4AF37]/70" />
              The music room runs on ChatGPT. You may be asked to sign in with a free ChatGPT account the first time you enter.
            </p>
          </motion.div>

          <motion.div initial={{ opacity: 0, scale: 0.96 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1, delay: 0.2 }} className="lg:col-span-5">
            <div className="grid grid-cols-3 gap-2 sm:gap-3 rotate-[-3deg]" data-testid="wolf-dawg-cover-wall">
              {COVERS.map((c, i) => (
                <img key={c} src={c} alt="WOLF release" loading="lazy" className={`w-full aspect-square object-cover border border-[#D4AF37]/30 ${i % 2 ? "translate-y-4" : ""}`} />
              ))}
            </div>
            <p className="mt-8 text-[10px] uppercase tracking-[0.3em] text-white/30 text-center">The collection · Every release · One room</p>
          </motion.div>
        </div>

        <div className="mt-24 sm:mt-32 grid sm:grid-cols-3 gap-6" data-testid="wolf-dawg-features">
          {FEATURES.map(({ icon: Icon, title, text }, i) => (
            <motion.div
              key={title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.12 }}
              className="border border-white/10 hover:border-[#D4AF37]/40 bg-[#0d0d0d] p-8 transition-colors"
            >
              <Icon size={22} className="text-[#D4AF37]" />
              <p className="font-display text-2xl tracking-wider text-white mt-5">{title}</p>
              <p className="text-white/55 text-base mt-3 leading-relaxed">{text}</p>
            </motion.div>
          ))}
        </div>

        <SongTabsSection />
      </div>
    </div>
  );
}
